import { useIonToast } from '@ionic/react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppMode,
  useAppModeContext,
} from '../../components/app/app-mode-provider';
import { trackMachineOfflineToastIgnore } from '../../config/analytics';
import { useDirectConnectCurrentMachineHealth } from '../../direct-connect/gate/direct-connect-health-gate';
import { Machine } from '../graphql/generated';

interface MachineOfflineToastResult {
  showMachineOfflineToast: () => void;
}

const useMachineOfflineToast = (): MachineOfflineToastResult => {
  const [showToast] = useIonToast();
  const { i18n } = useTranslation();

  const [ignore, setIgnore] = useState<boolean>(false);

  const showMachineOfflineToast = useCallback(() => {
    if (ignore === false) {
      showToast({
        message: i18n.t('common:robot_offline_will_sync_later'),
        duration: 3500,
        color: 'danger',
        position: 'top',
        buttons: [
          {
            text: i18n.t('common:ignore'),
            handler: () => {
              setIgnore(true);
              trackMachineOfflineToastIgnore();
            },
          },
        ],
      });
    }
  }, [showToast, i18n, ignore]);

  return { showMachineOfflineToast };
};

/**
 * This hook provides a centralized way to determine if a machine is connected
 * regardless of connection type.
 *
 * The `machine.state.online` property is sufficient for cloud since it reflects
 * the machine's connection to IoT, but direct connect is concerned with
 * connecting via Wi-Fi and passing health checks.
 */
const useMachineConnected = (params: { machine?: Machine }): boolean => {
  const { machine } = params;

  const { mode } = useAppModeContext();
  const directConnectMachineHealth = useDirectConnectCurrentMachineHealth();

  const isConnected = useMemo(() => {
    if (machine?.id === undefined) {
      return false;
    }

    if (mode === AppMode.CLOUD) {
      return machine.state?.online === true;
    }

    if (machine.id === directConnectMachineHealth?.machine?.machineId) {
      return true;
    }

    return false;
  }, [
    mode,
    machine?.id,
    machine?.state?.online,
    directConnectMachineHealth?.machine?.machineId,
  ]);

  return isConnected;
};

export { useMachineOfflineToast, useMachineConnected };
